import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { ChevronRight, FolderOpen, Plus, Search } from "lucide-react";
import { useState } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { projectsQueryOptions } from "~/lib/projects/queries";

export const Route = createFileRoute("/_auth/dashboard/")({
  head: () => ({
    meta: [{ title: "Dashboard | Canopy" }],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(projectsQueryOptions());
  },
  component: DashboardIndex,
});

function DashboardIndex() {
  const { data: projects } = useSuspenseQuery(projectsQueryOptions());
  const [search, setSearch] = useState("");

  const filteredProjects = projects.filter((project) =>
    project.title.toLowerCase().includes(search.toLowerCase()),
  );
  const hasProjects = projects.length > 0;

  return (
    <main className="mx-auto w-full max-w-5xl px-6 py-8">
        <div className="animate-fade-in-up flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-baseline gap-3">
            <h1 className="text-foreground text-xl font-semibold">Projects</h1>
            {hasProjects && (
              <span className="text-muted-foreground text-sm">
                {projects.length} project{projects.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="flex w-full items-center gap-3 sm:w-auto">
            <div className="relative flex-1 sm:flex-initial">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                placeholder="Search projects..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 sm:w-64"
              />
            </div>
            <Button render={<Link to="/projects/new" />} className="gap-2" nativeButton={false}>
              <Plus className="size-4" />
              <span className="hidden sm:inline">New project</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>

        <div className="stagger-1 mt-6">
          {hasProjects ? (
            filteredProjects.length > 0 ? (
              <div className="divide-y rounded-xl border">
                {filteredProjects.map((project) => (
                  <Link
                    key={project.id}
                    to="/projects/$projectId"
                    params={{ projectId: project.id }}
                    className="hover:bg-accent/50 flex items-center gap-4 px-4 py-3 transition-colors"
                  >
                    <div className="bg-muted text-muted-foreground flex-shrink-0 rounded-md p-2">
                      <FolderOpen className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-foreground truncate text-sm font-medium">
                        {project.title}
                      </p>
                      {project.description && (
                        <p className="text-muted-foreground mt-0.5 truncate text-xs">
                          {project.description}
                        </p>
                      )}
                    </div>
                    <span className="text-muted-foreground hidden flex-shrink-0 text-xs whitespace-nowrap sm:block">
                      {formatDistanceToNow(project.createdAt, { addSuffix: true })}
                    </span>
                    <ChevronRight className="text-muted-foreground/50 size-4 flex-shrink-0" />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed p-12 text-center">
                <p className="text-muted-foreground text-sm">
                  No projects matching "{search}"
                </p>
              </div>
            )
          ) : (
            <EmptyState />
          )}
        </div>
      </main>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed p-12 text-center">
      <div className="mx-auto max-w-sm">
        <div className="bg-muted mx-auto mb-4 inline-flex rounded-full p-3">
          <FolderOpen className="text-muted-foreground size-6" />
        </div>
        <h3 className="text-foreground text-lg font-medium">No projects yet</h3>
        <p className="text-muted-foreground mt-2 text-sm">
          Create a systematic review project to organize your research and manage citations.
        </p>
        <Button render={<Link to="/projects/new" />} size="lg" className="mt-6 gap-2" nativeButton={false}>
          <Plus className="size-4" />
          Create Project
        </Button>
      </div>
    </div>
  );
}
